/**
 * BUILDLIST 69.73 step 2a (DECISIONS 301, 303 (1); ARCHITECTURE §5.34): planetary intel — one record per side of what it knows
 * of the other's things (`GameState.intel`), stepped on its own, with GameConfig.intelRecord.
 * - **Structures and vehicles**, by sight: each tick, the enemy structures whose id is the tick (mod INTEL.passTicks) are
 *   looked at by every side's eyes — its people (within INTEL.sightPeople, the weather's sight within it), its flying drones
 *   (inside their ground circle) and its standing towers — at the thing's near side, past its footprint. Seen: its entry takes
 *   the place, the tick, how (seen, drone, tower) and its state (down: destroyed, its wreck seen). A structure no longer in the
 *   state is believed where it was until its place is looked at and seen empty (gone). Spread so (§5.32), a thing is looked
 *   at once every INTEL.passTicks and no tick does them all.
 * - **Bases**: a base is known where any of its buildings (its HQ, pad, dump) is; its entry is the freshest of theirs.
 * - **People**: the side's picture of the other's people (core/picture.ts teamPicture: its look, its reports, its sensors, its
 *   marks) folds in every INTEL.unitsTicks, replacing the last.
 * The commander reads all of it (intelOf). A bot reads the record's slow kinds only through its orders (303's share: its own
 * and its squad's sight and the team's tracks, as before, near and fast). Off, nothing runs and `state.intel` stays absent.
 * Pure and deterministic: entries in kind, then id order; the looks in id order.
 */
import type { GameState, IntelEntry, IntelKind, IntelSource, StructureState, Vec3 } from '../types';
import { INTEL, PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { personSetOf } from './scaleProfile';
import { arcDistance } from './gameState';
import { structureTeam, drives } from './body';
import { isSite } from './pointDefence';
import { structureFootprint } from './buildingScale';
import { personEyePoint, personPointOn, personLineOfSight } from './fineTerrain';
import { weatherSees } from './perception';
import { weatherEffectHere } from './weather';
import { dronePos, droneGroundDir } from './drones';
import { isTowerSensor, towerTeam, towerEyePoint } from './towers';
import { teamPicture, type Belief } from './picture';
import { padSpotDir, parkedId } from './helipad';   // 69.74 step 2c
import { dacos, dcos, dsin } from './dmath';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub, vecCross } from '../math';

const KIND_ORDER: Record<IntelKind, number> = { base: 0, site: 1, structure: 2, vehicle: 3, aircraft: 4, unit: 5 };
const byKindId = (a: IntelEntry, b: IntelEntry) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || (a.id ?? -1) - (b.id ?? -1) || a.tick - b.tick;
const PICTURE_SOURCE: Record<Belief['source'], IntelSource> = { seen: 'seen', told: 'told', drone: 'drone', sensor: 'tower', mark: 'mark' };

/** What an enemy thing is in the record: a vehicle, an air-defence site, or a structure. */
export function intelKindOf(s: Pick<StructureState, 'type' | 'drive' | 'pd'>): IntelKind {
  return drives(s) ? 'vehicle' : isSite(s) ? 'site' : 'structure';
}

/**
 * Does one of `team`'s eyes see the point `body` (a thing's middle at a person's eye height) now — its people within
 * INTEL.sightPeople and the weather's sight, its flying drones over it, its standing towers — with a line to its near side,
 * `near` short of it? How: 'seen' (a person), 'drone', 'tower'; null: unseen.
 */
function seenBy(state: GameState, team: number, planetIndex: number, dir: Vec3, near: number, tick: number): IntelSource | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, seconds = tick * PHYS_DT;
  const body = personPointOn(planet, dir, ps, ps.losEyeHeight);
  const sees = (eye: Vec3) => {
    const to = vecSub(eye, body), L = vecLength(to);
    return L <= 2 * near || personLineOfSight(eye, vecAdd(body, vecScale(to, near / L)), state);
  };
  for (const u of state.players) {
    if (!u.alive || u.teamIndex !== team || u.planetIndex !== planetIndex || (u.landingLeft ?? 0) > 0 || u.boarded) continue;
    if (arcDistance(u.surfaceDir, dir, planet.radius) > INTEL.sightPeople * person) continue;
    const eye = personEyePoint(u, state.planets, ps, ps.losEyeHeight);
    const sight = weatherEffectHere(state, planetIndex, u.surfaceDir, seconds).sight * ps.k;
    if (vecLength(vecSub(body, eye)) > sight) continue;
    if (weatherSees(state, planetIndex, eye, body, seconds, sight) && sees(eye)) return 'seen';
  }
  const ground = vecNormalize(dir);
  for (const d of state.drones ?? []) {
    if (!d.alive || d.teamIndex !== team || d.planetIndex !== planetIndex) continue;
    if (dacos(Math.max(-1, Math.min(1, vecDot(droneGroundDir(d, planet.radius), ground)))) * planet.radius > ps.droneSightRadius) continue;
    if (sees(dronePos(d, state))) return 'drone';
  }
  for (const s of state.structures) {
    if (!isTowerSensor(s) || s.planetIndex !== planetIndex || towerTeam(s, state.players) !== team) continue;
    if (sees(towerEyePoint(s, state))) return 'tower';
  }
  return null;
}

/** The near side of a structure: its footprint and INTEL.nearPeople past it (world units). */
const nearOf = (state: GameState, s: Pick<StructureState, 'type' | 'builder' | 'planetIndex'>): number =>
  structureFootprint(s, state.config, state.planets[s.planetIndex]?.radius ?? 1) + INTEL.nearPeople * PERSON_BODY_HEIGHT * personSetOf(state).k;

/** The sides in play, in order. */
const teamsOf = (state: GameState): number[] => [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);

/** One tick of every side's record (match.ts, each tick with the switch on): this tick's share of the looks, and on its beat the people. */
export function stepIntel(state: GameState, tick: number): GameState {
  if (!state.config.intelRecord) return state;
  const slot = tick % INTEL.passTicks, unitsDue = tick % INTEL.unitsTicks === 0;
  const look = state.structures.filter(s => s.id % INTEL.passTicks === slot);
  const lookGone = (e: IntelEntry) => e.id !== undefined && e.kind !== 'unit' && e.kind !== 'base' && e.kind !== 'aircraft' && e.state !== 'gone' && e.id % INTEL.passTicks === slot;   // (69.74 step 2c: a parked helicopter's looked at with its pad)
  if (!look.length && !unitsDue && !Object.values(state.intel ?? {}).some(es => es.some(lookGone))) return state;
  let intel = state.intel;
  for (const team of teamsOf(state)) {
    const before = intel?.[team] ?? [];
    let list = before;
    const put = (e: IntelEntry) => { list = [...list.filter(x => !(x.kind === e.kind && x.id === e.id)), e]; };
    // the structures and vehicles due this tick
    for (const s of look) {
      const owner = structureTeam(state, s);
      if (owner === null || owner === team) continue;
      const how = seenBy(state, team, s.planetIndex, s.surfaceDir, nearOf(state, s), tick);
      if (!how) continue;
      put({ kind: intelKindOf(s), id: s.id, owner, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, tick, source: how, ...(s.health <= 0 ? { state: 'down' as const } : {}) });
      if (s.type === 'helipad') for (const e of padAircraft(state, owner, s, before, how, tick)) put(e);   // 69.74 step 2c
    }
    // a thing believed where it was, no longer there: its place seen empty
    for (const e of before) {
      if (!lookGone(e) || state.structures.some(x => x.id === e.id)) continue;
      const how = seenBy(state, team, e.planetIndex, e.surfaceDir, INTEL.nearPeople * PERSON_BODY_HEIGHT * personSetOf(state).k, tick);
      if (how) put({ ...e, tick, source: how, state: 'gone' });
    }
    // the people, on their beat: the side's picture, replacing the last
    if (unitsDue) {
      const units: IntelEntry[] = teamPicture(state, team, tick).filter(b => b.unitTeam === undefined || b.unitTeam !== team)
        .map(b => ({ kind: 'unit' as const, ...(b.unitId !== undefined ? { id: b.unitId } : {}), owner: b.unitTeam ?? -1, planetIndex: b.planetIndex, surfaceDir: b.surfaceDir, tick: b.tick, source: PICTURE_SOURCE[b.source] }));
      if (units.length || list.some(x => x.kind === 'unit')) list = [...list.filter(x => x.kind !== 'unit'), ...units];   // (nothing known, nothing written)
    }
    if (list === before) continue;
    // the bases: known where any of their buildings is, as fresh as the freshest — a rough area (step 3) kept until then
    const found = basesKnown(state, team, list);
    list = [...list.filter(x => x.kind === 'base' && x.spread !== undefined && !found.some(f => f.owner === x.owner)), ...list.filter(x => x.kind !== 'base'), ...found].sort(byKindId);
    intel = { ...intel, [team]: list };
  }
  return intel === state.intel ? state : { ...state, intel };
}

/**
 * 69.74 step 2c: a helipad seen is its helicopters seen — each spot's (its id the parked body's): one on it standing, one lost there
 * down (its wreck), and one known before and out now gone (its spot seen empty).
 */
function padAircraft(state: GameState, owner: number, s: StructureState, before: readonly IntelEntry[], source: IntelSource, tick: number): IntelEntry[] {
  const pad = state.dropships?.pads?.[owner];
  if (!pad || pad.structureId !== s.id) return [];
  const out: IntelEntry[] = [];
  pad.spots.forEach((x, k) => {
    const id = parkedId(owner, k), base = { kind: 'aircraft' as const, id, owner, planetIndex: s.planetIndex, surfaceDir: padSpotDir(state, s, k, pad.spots.length), tick, source };
    if (x.state === 'ready' || x.state === 'turn') out.push(base);
    else if (x.state === 'lost') out.push({ ...base, state: 'down' });
    else if (before.some(e => e.kind === 'aircraft' && e.id === id && e.state === undefined)) out.push({ ...base, state: 'gone' });
  });
  return out;
}

/** The other sides' bases `team` knows: each where any of its buildings (HQ, pad, dump) is in `list`, from the freshest of them. */
function basesKnown(state: GameState, team: number, list: readonly IntelEntry[]): IntelEntry[] {
  const out: IntelEntry[] = [];
  for (const b of state.board?.bases ?? []) {
    if (b.team === team || b.hq === undefined) continue;
    const ids = [b.hq, b.pad, b.dump].filter((x): x is number => x !== undefined);
    const seen = list.filter(e => e.kind !== 'unit' && e.kind !== 'base' && e.id !== undefined && ids.includes(e.id)).sort((x, y) => y.tick - x.tick)[0];
    if (seen) out.push({ kind: 'base', id: b.hq, owner: b.team, planetIndex: b.planetIndex, surfaceDir: b.dir, tick: seen.tick, source: seen.source });
  }
  return out;
}

/** The commander's read: all of `team`'s record (empty with the switch off, or before it knows anything). */
export function intelOf(state: Pick<GameState, 'intel'>, team: number): readonly IntelEntry[] {
  return state.intel?.[team] ?? [];
}

/** What `team` knows of structure (or vehicle) `id`: its entry, or null (not known, or its place seen empty). */
export function knownStructure(state: Pick<GameState, 'intel'>, team: number, id: number): IntelEntry | null {
  return intelOf(state, team).find(e => e.kind !== 'unit' && e.kind !== 'base' && e.id === id && e.state !== 'gone') ?? null;
}

/** What `team` knows of side `owner`'s base: its entry (found: where its buildings are), or null — a rough area is not found (baseArea). */
export function knownBase(state: Pick<GameState, 'intel'>, team: number, owner: number): IntelEntry | null {
  return intelOf(state, team).find(e => e.kind === 'base' && e.owner === owner && e.spread === undefined) ?? null;
}

/** 69.73 step 3: the rough area `team` has for side `owner`'s base not yet found (GameConfig.baseIntel 'area'), or null. */
export function baseArea(state: Pick<GameState, 'intel'>, team: number, owner: number): IntelEntry | null {
  return intelOf(state, team).find(e => e.kind === 'base' && e.owner === owner && e.spread !== undefined) ?? null;
}

/**
 * 69.73 step 3 (DECISIONS 303 (2); setup, with intelRecord): what each side knows of the other's base at the start, by
 * GameConfig.baseIntel — `known`: its HQ, pad and dump (source `start`), and so the base; `area`: a ring INTEL.areaShare of the
 * bases' distance across round a middle INTEL.areaOffset of that off the true base, on a bearing taken from the base's own
 * place (no draw: the same board, the same ring); `none` or absent: nothing. Pure.
 */
export function seedBaseIntel(state: GameState): GameState {
  const how = state.config.baseIntel ?? 'none';
  if (!state.config.intelRecord || how === 'none' || !state.board) return state;
  let intel = state.intel ?? {};
  for (const team of teamsOf(state)) {
    let list: IntelEntry[] = [...(intel[team] ?? [])];
    for (const b of state.board.bases) {
      if (b.team === team || b.hq === undefined) continue;
      const planet = state.planets[b.planetIndex];
      if (!planet) continue;
      if (how === 'known') {
        for (const id of [b.hq, b.pad, b.dump]) {
          const s = id === undefined ? undefined : state.structures.find(x => x.id === id);
          if (s) list.push({ kind: intelKindOf(s), id: s.id, owner: b.team, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, tick: 0, source: 'start' });
        }
        list = [...list.filter(x => x.kind !== 'base' || x.owner !== b.team), ...basesKnown(state, team, list)];
      } else {
        const other = state.board.bases.find(x => x.team === team);
        const apart = other ? arcDistance(other.dir, b.dir, planet.radius) : planet.radius;
        const spread = INTEL.areaShare * apart;
        // the middle: off the base along a bearing from the base's own direction (its components' fractions), by a share of the ring
        const n = vecNormalize(b.dir), ax = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
        const e1 = vecNormalize(vecCross(ax, n)), e2 = vecCross(n, e1);
        const turn = 2 * Math.PI * (((Math.abs(b.dir.x) + Math.abs(b.dir.y) * 7 + Math.abs(b.dir.z) * 13) * 1000) % 1);
        const t = vecAdd(vecScale(e1, dcos(turn)), vecScale(e2, dsin(turn))), arc = (INTEL.areaOffset * spread) / planet.radius;
        const mid = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(t, dsin(arc))));
        list.push({ kind: 'base', id: b.hq, owner: b.team, planetIndex: b.planetIndex, surfaceDir: mid, tick: 0, source: 'start', spread });
      }
    }
    if (list.length) intel = { ...intel, [team]: list.sort(byKindId) };
  }
  return { ...state, intel };
}
